/**
 * WelcomeCard -- replaces the Today tab empty state when onboarding is incomplete.
 *
 * Shows a progress checklist and links to open the setup wizard or chat.
 */

import type { UserProfile } from "../layout/sidebar/types";

interface WelcomeCardProps {
  profile: UserProfile | null;
  onOpenChat: () => void;
  onOpenWizard: () => void;
}

interface ChecklistItem {
  label: string;
  done: boolean;
}

function getChecklist(profile: UserProfile | null): ChecklistItem[] {
  return [
    { label: "Set your name", done: !!profile?.name },
    {
      label: "Set timezone & hours",
      done: !!(profile?.timezone && profile?.workingHours?.start && profile?.workingHours?.end),
    },
    { label: "Create a project", done: (profile?.projects?.length ?? 0) > 0 },
  ];
}

export function WelcomeCard({ profile, onOpenChat, onOpenWizard }: WelcomeCardProps): React.ReactElement {
  const checklist = getChecklist(profile);
  const completedCount = checklist.filter((item) => item.done).length;
  const progressPercent = (completedCount / 3) * 100;

  return (
    <div className="flex flex-col items-center py-8 px-4 gap-4">
      <h2 className="text-lg font-semibold text-text-primary">Welcome to Atlas</h2>
      <p className="text-sm text-text-secondary text-center">
        I'm your AI task manager. Here's how to get started:
      </p>

      {/* Checklist */}
      <div className="w-full max-w-xs space-y-2">
        {checklist.map((item) => (
          <div key={item.label} className="flex items-center gap-2">
            {item.done ? (
              <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-text-tertiary flex-shrink-0" />
            )}
            <span className={`text-sm ${item.done ? "text-text-tertiary line-through" : "text-text-primary"}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-xs h-1.5 bg-surface-hover rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={onOpenWizard}
          className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
        >
          Continue Setup
        </button>
        <button
          type="button"
          onClick={onOpenChat}
          className="px-4 py-2 text-xs font-medium text-primary bg-primary-subtle hover:bg-primary/20 rounded-lg transition-colors"
        >
          Chat with Atlas
        </button>
      </div>

      <p className="text-xs text-text-tertiary text-center">
        Or just start chatting -- I'll guide you through it
      </p>
    </div>
  );
}
